import isEmpty from 'lodash/isEmpty';
import { Chart } from '@hc/chart-dot-jsx';
import { NullStateCard } from '@hc/component-lib/hclib/components/molecules/null-state-card';

import { NULL_STATE_WARNINGS } from 'legacy/appstore/constants';

import { numberWithPlaceholderFormatter } from 'legacy/utils/formatters';

import errorBoundaryNullStateCard from 'legacy/hocs/errorBoundaryNullStateCard';
import RiskOfDeclineChart from 'legacy/components/RiskOfDeclineChart';
import Loading from 'legacy/components/generic/Loading';
import GlossaryButton from 'legacy/components/GlossaryButton';

import { MarketAnalysisSection as theme } from 'legacy/css-modules';

const DaysOnMarketChart = errorBoundaryNullStateCard(
  Chart,
  'daysOnMarketChart'
);
const MonthsOfSupplyChart = errorBoundaryNullStateCard(
  Chart,
  'monthsOfSupplyChart'
);

const ChartTitle = ({ title, terms, dataHcName, handleClickInfoButton }) => {
  return (
    <div data-hc-name={`${dataHcName}-title`} className={theme.ChartTitle}>
      <div className={theme.ChartTitleText}>{title}</div>
      <GlossaryButton
        height="16px"
        width="16px"
        dataHcName={`${dataHcName}-info-button`}
        className={theme.infoButton}
        onClick={() => handleClickInfoButton(terms)}
      />
    </div>
  );
};

const MarketAnalysisStats = ({ stats, dataHcName }) => {
  return (
    <div className={theme.statSection}>
      {stats.map((stat) => {
        return (
          <div className={theme.statCell} key={`market-stats--${stat.label}`}>
            <div
              className={theme.statLabel}
              data-hc-name={`${dataHcName}-stat-label`}
            >
              {stat.label}
            </div>
            <div
              className={theme.statValue}
              data-hc-name={`${dataHcName}-stat-value`}
            >
              {stat.value}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const MarketAnalysisSection = ({
  forecastModels,
  handleClickInfoButton,
  isReportLoaded,
  hasMarketSectionData,
  hasDataDaysOnMarket,
  hasDataMonthsOfSupply,
  hasDataRiskOfDecline,
  chartConfigDaysOnMarket,
  chartConfigMonthsOfSupply,
  chartDataRiskOfDecline,
  isEffectiveDateReport
}) => {
  if (isEffectiveDateReport) return null;
  return (
    <div data-hc-name="market-analysis-section">
      <div>
        <div className={theme.chartRow}>
          <ChartTitle
            title="MSA Risk of Decline"
            dataHcName="risk-of-decline"
            terms={['RISK_OF_DECLINE']}
            handleClickInfoButton={handleClickInfoButton}
          />
          <Loading loading={!isReportLoaded} overlay>
            {hasDataRiskOfDecline || !isReportLoaded ? (
              <div>
                <RiskOfDeclineChart
                  dataHcName="risk-of-decline-chart"
                  {...chartDataRiskOfDecline}
                />
                {!isEmpty(forecastModels) && (
                  <MarketAnalysisStats
                    dataHcName="risk-of-decline-chart"
                    stats={[
                      {
                        label: 'This month last year',
                        value: `${numberWithPlaceholderFormatter(
                          forecastModels.riskLastYr,
                          { precision: 1 }
                        )}%`
                      },
                      {
                        label: 'This month',
                        value: `${numberWithPlaceholderFormatter(
                          forecastModels.riskCurrent,
                          { precision: 1 }
                        )}%`
                      },
                      {
                        label: 'Risk Level',
                        value:
                          forecastModels.riskScore &&
                          (forecastModels.riskScore.replace('_', ' ') || '--')
                      }
                    ]}
                  />
                )}
              </div>
            ) : (
              <NullStateCard
                title={NULL_STATE_WARNINGS.riskOfDeclineChart.title}
                className={theme.NullStateCard}
              >
                {NULL_STATE_WARNINGS.riskOfDeclineChart.message}
              </NullStateCard>
            )}
          </Loading>
        </div>
        {hasMarketSectionData || !isReportLoaded ? (
          <div>
            <div className={theme.chartRow}>
              <div className={theme.chartCell}>
                <ChartTitle
                  title="Days on Market - Sold or De-Listed Properties"
                  dataHcName="dom"
                  terms={['DAYS_ON_MARKET']}
                  handleClickInfoButton={handleClickInfoButton}
                />
                <Loading loading={!isReportLoaded} overlay>
                  {!isReportLoaded || hasDataDaysOnMarket ? (
                    <DaysOnMarketChart chartConfig={chartConfigDaysOnMarket} />
                  ) : (
                    <NullStateCard
                      title={NULL_STATE_WARNINGS.monthsOfSupplyChart.title}
                      className={theme.NullStateCard}
                    >
                      {NULL_STATE_WARNINGS.monthsOfSupplyChart.message}
                    </NullStateCard>
                  )}
                </Loading>
              </div>

              <div className={theme.chartCell}>
                <ChartTitle
                  title="Months of Supply"
                  dataHcName="mos"
                  terms={['MONTHS_OF_SUPPLY']}
                  handleClickInfoButton={handleClickInfoButton}
                />
                <Loading loading={!isReportLoaded} overlay>
                  {!isReportLoaded || hasDataMonthsOfSupply ? (
                    <MonthsOfSupplyChart
                      chartConfig={chartConfigMonthsOfSupply}
                    />
                  ) : (
                    <NullStateCard
                      title={NULL_STATE_WARNINGS.monthsOfSupplyChart.title}
                      className={theme.NullStateCard}
                    >
                      {NULL_STATE_WARNINGS.monthsOfSupplyChart.message}
                    </NullStateCard>
                  )}
                </Loading>
              </div>
            </div>
          </div>
        ) : (
          <NullStateCard
            title={NULL_STATE_WARNINGS.marketCharts.title}
            className={theme.NullStateCard}
          >
            {NULL_STATE_WARNINGS.marketCharts.message}
          </NullStateCard>
        )}
      </div>
    </div>
  );
};

export default errorBoundaryNullStateCard(
  MarketAnalysisSection,
  'marketCharts'
);
